import React from 'react'
import { Helmet } from 'react-helmet'
import Layout from '../components/layout'
import Tag from '../components/tag'
import * as style from './templates.module.css'

const TemplatesPage = () => {

  return (
    <Layout>
      <Helmet>
        <title>Vault</title>
        <meta name="description" content="Get templates and premium content for better thinking" />
      </Helmet>

      <main className={style.hero}>
        <div className={style.heroContent}>
          <div className={style.heroCenter}>
            <h1>Complete thinking toolkit for tech leaders, consultants and founders</h1>
            <h2 className={style.heroSubhead}>Set of toolkits for confident decision-making, problem-solving and applying the right thinking tools in your work and life.</h2>
            
            <div className={style.heroCta}>
              <a href="#pricing" className={`${style.heroCtaButton} plausible-event-name=vaultCTA`} onClick={() => console.log('Hero CTA clicked')}>
                Get the premium toolkit
              </a>
              <div className={style.seeInsideLink}>
                <a href="#whats-inside" className={style.seeInsideButton}>
                  What's inside
                  <span className={style.downArrow}>↓</span>
                </a>
              </div>
            </div>
            
            <div className={style.heroImage}>
              <img src="/vault-preview.png" alt="Untools Vault Preview" />
            </div>
          </div>
        </div>
      </main>

      <section className={style.valuePropSection}>
        <div className={style.valuePropContent}>
          <div className={style.valuePropLeft}>
            <h2>Learn to apply the right thinking tools</h2>
            <p>Most people rely on the same approach for every problem - usually gut feeling mixed with endless deliberation. That works sometimes, but it's inconsistent and stressful.</p>
            <p>With the right thinking tools, you can:</p>
            <ul>
              <li><strong>For Consultants:</strong> Use frameworks to structure your client engagements</li>
              <li><strong>For Tech Leads & PMs:</strong> Make better product decisions and align stakeholders</li>
              <li><strong>For Founders:</strong> Use systems to navigate uncertainty and solve complex problems</li>
              <li><strong>For Everyone:</strong> Find solutions you might not have considered</li>
            </ul>
          </div>
          <div className={style.valuePropRight}>
            <div className={style.testimonial}>
              <div className={style.testimonialContent}>
                <p>"I used the templates for my team of consultants to help them with frameworks on addressing some of the problems we faced. They helped me and my team to simplify the approach and engage our stakeholders in a more meaningful way."</p>
                <div className={style.testimonialAuthor}>– Rafael, consultant</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="whats-inside" className={style.whatsInsideSection}>
        <div className={style.whatsInsideContent}>
          <div className={style.whatsInsideText}>
            <h2>What you get in Untools Vault</h2>
            <p>Complete library of 7 toolkits with dozens of resources. <br/>Each toolkit includes guides on choosing the right tool and ready-to-use templates in PDF, Miro and Notion.</p>
            
            <div className={style.whatsInsidePoints}>
              <div className={style.whatsInsidePoint}>
                <div className={style.whatsInsidePointContent}>
                  <div className={style.toolkitsVertical}>
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>Decision-making toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>Decision matrix, Eisenhower matrix, Hard choice model, and more</li>
                        <li>6 frameworks for confident choices</li>
                      </ul>
                    </div>
                    
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>Problem-solving toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>First principles, Issue trees, Abstraction laddering, and more</li>
                        <li>5 systematic approaches to find solutions</li>
                      </ul>
                    </div>
                    
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>Systems thinking toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>Connection circles, Iceberg model, Feedback loops, and more</li>
                        <li>4 tools for understanding complexity</li>
                      </ul>
                    </div>
                    
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>Communication & leadership toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>Situation-Behavior-Impact, Conflict resolution, Minto Pyramid</li>
                        <li>3 frameworks for better discussions</li>
                      </ul>
                    </div>
                    
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>AI collaboration toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>Prompt engineering 101, Collaboration techniques and Thinking tools templates</li>
                        <li>9 techniques and templates for thinking with AI</li>
                      </ul>
                    </div>
                    
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>Facilitation toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>Frameworks for effective meetings and workshops</li>
                        <li>Tools to guide group discussions</li>
                      </ul>
                    </div>
                    
                    <div className={style.toolkitVerticalItem}>
                      <div className={style.toolkitVerticalHeader}>
                        <h3>Retrospectives toolkit</h3>
                      </div>
                      <ul className={style.toolkitVerticalList}>
                        <li>Techniques for team improvement and learning</li>
                        <li>Templates for running retrospectives</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className={style.whatsInsidePointImage}>
                  <img src="/Vault-LP-preview-2.png" alt="Vault templates preview" />
                  <img src="/Vault-LP-preview-3.png" alt="Vault monthly content preview" />
                  <img src="/Vault-LP-preview-1.png" alt="Vault deep dives preview" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className={style.pricingSection}>
        <div className={style.pricingContent}>
          <h2>Choose your toolkit</h2>
          <div className={style.pricingGrid}>
            <div className={style.pricingCard}>
              <div className={style.pricingCardHeader}>
                <h3>Untools Worksheets</h3>
                <div className={style.pricingPrice}>$35</div>
                <div className={style.pricingImage}>
                  <img src="/pdf-worksheets.png" alt="Untools Worksheets" />
                </div>
              </div>
              <div className={style.pricingFeatures}>
                <ul className={style.pricingChecklist}>
                  <li>
                    <span className={style.checkIcon}>✓</span>
                    <span>
                      <span className={style.popoverTrigger}>
                        10 essential templates in PDF
                        <div className={style.popover}>
                          <div className={style.popoverContent}>
                            <h4>Templates included:</h4>
                            <ul>
                              <li>Abstraction Laddering</li>
                              <li>Connection Circles</li>
                              <li>Eisenhower Matrix</li>
                              <li>Decision Matrix</li>
                              <li>Iceberg Model</li>
                              <li>Impact-Effort Matrix</li>
                              <li>Ladder of Inference</li>
                              <li>Second-Order Thinking</li>
                              <li>Conflict Resolution Diagram</li>
                              <li>Situation-Behavior-Impact</li>
                            </ul>
                          </div>
                        </div>
                      </span>
                    </span>
                  </li>
                  <li>
                    <span className={style.checkIcon}>✓</span>
                    <span>Lifetime access</span>
                  </li>
                  <li className={style.unavailable}>
                    <span className={style.crossIcon}>×</span>
                    <span>Member-only content</span>
                  </li>
                  <li className={style.unavailable}>
                    <span className={style.crossIcon}>×</span>
                    <span>Free updates</span>
                  </li>
                </ul>
              </div>
              <div className={style.pricingCta}>
                <a href="https://untools.gumroad.com/l/untools-worksheets?wanted=true" target="_blank" rel="noopener noreferrer" className={style.pricingButtonSecondary}>
                  Get Worksheets
                </a>
              </div>
            </div>

            <div className={`${style.pricingCard} ${style.pricingCardFeatured}`}>
              <div className={style.bestValueBadge}>
                <Tag category="💎 Best value" />
              </div>
              <div className={style.pricingCardHeader}>
                <h3>Untools Vault</h3>
                <div className={style.pricingPrice}>$99</div>
                <div className={style.pricingImage}>
                  <img src="/vault-preview.png" alt="Untools Vault" />
                </div>
              </div>
              <div className={style.pricingFeatures}>
                <ul className={style.pricingChecklist}>
                  <li>
                    <span className={style.checkIcon}>✓</span>
                    <span>
                      <span className={style.popoverTrigger}>
                        20 templates in PDF, Miro or Notion
                        <div className={style.popover}>
                          <div className={style.popoverContent}>
                            <h4>Templates included:</h4>
                            <ul>
                              <li>Abstraction Laddering</li>
                              <li>Connection Circles</li>
                              <li>Eisenhower Matrix</li>
                              <li>Decision Matrix</li>
                              <li>Iceberg Model</li>
                              <li>Impact-Effort Matrix</li>
                              <li>Ladder of Inference</li>
                              <li>Second-Order Thinking</li>
                              <li>Conflict Resolution Diagram</li>
                              <li>Situation-Behavior-Impact</li>
                              <li>Six Thinking Hats</li>
                              <li>Ishikawa Diagram</li>
                              <li>Zwicky box</li>
                              <li>Plus templates for Facilitation & Retrospectives</li>
                            </ul>
                          </div>
                        </div>
                      </span>
                    </span>
                  </li>
                  <li>
                    <span className={style.checkIcon}>✓</span>
                    <span>Lifetime access</span>
                  </li>
                  <li>
                    <span className={style.checkIcon}>✓</span>
                    <span>
                      <strong>Member-only content:</strong><br/>
                      <span style={{fontSize: '1.4rem', opacity: 0.8}}>Decision-making under uncertainty, Creative problem-solving, Systems thinking 101, Communication and leadership toolkit, AI collaboration toolkit, Facilitation 101, Retrospectives frameworks, and more.</span>
                    </span>
                  </li>
                  <li>
                    <span className={style.checkIcon}>✓</span>
                    <span>Free updates</span>
                  </li>
                </ul>
              </div>
              <div className={style.pricingCta}>
                <a href="https://untools.gumroad.com/l/vault-lifetime?wanted=true" target="_blank" rel="noopener noreferrer" className={style.pricingButtonPrimary}>
                  Get Vault
                </a>
              </div>
            </div>
          </div>
          <div className={style.pricingGuarantee}>
            Pay once, no subscription, 14 days money-back guarantee
          </div>
        </div>
      </section>

      <section className={style.faqSection}>
        <h2>Frequently Asked Questions</h2>
        <div className={style.faqList}>
          <div className={style.faqItem}>
            <h3>What's the difference between Vault and Worksheets?</h3>
            <p>Vault is a lifetime membership that gives you access to all 15+ Untools templates also in Miro and Notion formats (and any future updates), and the library of premium deep dives and guides. Worksheets is a set of 10 PDF templates (without future updates).</p>
          </div>
          <div className={style.faqItem}>
            <h3>Is Vault a subscription or one-time payment?</h3>
            <p>Vault is a one-time payment of $99 for lifetime access. No recurring payments or subscriptions - you pay once and get access forever.</p>
          </div>
          <div className={style.faqItem}>
            <h3>What if I'm not happy with it?</h3>
            <p>There is a 14-day money-back guarantee on both Vault and Worksheets. If you're not happy, just get in touch and I'll refund you.</p>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default TemplatesPage
